import type { ScriptBlockData } from '../components/story/ScriptBlock';
import type {PrintLine} from './scriptLayout';

/** Text and paragraph attributes match the observed FDX serialization names.
 * Content remains the plain-text projection, checked against these ordered runs. */
export interface ScriptTextRun { text: string; attributes: Record<string, string> }
/** Supported Story Courier contract. Native FD13.1 00dd6280 also quantizes
 * sizes before font creation; this range and linear Courier model are ours. */
export function scriptFontSize(value:string|undefined,fallback=12):number {
    if(value===undefined||value==='')return fallback;
    if(!/^\d+(?:\.\d+)?$/.test(value.trim()))return fallback;
    const size=Number(value);
    return size>=6&&size<=24?Math.floor(size):fallback;
}
export function courierTextWidth(text:string,size=12):number {
    return Array.from(text).length*(size*6/10);
}
export interface ScriptRichText {
    schemaVersion: 1;
    runs: ScriptTextRun[];
    paragraphAttributes: Record<string, string>;
    // Story paragraph-local UTF-16 anchors, not native FDX Offset coordinates.
    deletionMarks?: {offset:number;revisionId:string}[];
}
export type TextStyle = 'Bold' | 'Italic' | 'Underline';
/** An observed browser/command replacement; before fences stale selections. */
export interface ScriptTextEdit { before:string; start:number; end:number; text:string }
export function observedTextEdit(before:string,after:string,start:number,end:number):ScriptTextEdit|undefined {
    if(!Number.isSafeInteger(start)||!Number.isSafeInteger(end)||start<0||end<start||end>before.length)return;
    const nextEnd=after.length-(before.length-end);
    if(nextEnd<start||before.slice(0,start)!==after.slice(0,start)||before.slice(end)!==after.slice(nextEnd))return;
    if(/[\uDC00-\uDFFF]/.test(before[start]??'')||/[\uDC00-\uDFFF]/.test(before[end]??''))return;
    return {before,start,end,text:after.slice(start,nextEnd)};
}
const record = (value: unknown): value is Record<string, string> => value !== null
    && typeof value === 'object' && !Array.isArray(value)
    && Object.values(value).every(item => typeof item === 'string');

export function validateRichText(value: unknown, content: string): asserts value is ScriptRichText | undefined {
    if (value === undefined) return;
    const data = value as ScriptRichText;
    if (!data || data.schemaVersion !== 1 || !record(data.paragraphAttributes)
        || !Array.isArray(data.runs) || data.runs.some(run => !run || typeof run.text !== 'string' || !record(run.attributes))
        || data.runs.map(run => run.text).join('') !== content) {
        throw new Error('Screenplay formatting does not match its text. Keep the recovery draft and use an updated editor.');
    }
    if(data.deletionMarks!==undefined && (!Array.isArray(data.deletionMarks)||data.deletionMarks.length>5000
        || data.deletionMarks.some(m=>!m||!Number.isSafeInteger(m.offset)||m.offset<0||m.offset>content.length
            || typeof m.revisionId!=='string'||!/^[1-9]\d{0,8}$/.test(m.revisionId)
            || /[\uDC00-\uDFFF]/.test(content[m.offset]??''))))throw new Error('Invalid screenplay deletion anchors. Keep the recovery draft.');
}
export function runsFor(block: ScriptBlockData): ScriptTextRun[] {
    validateRichText(block.richText, block.content);
    return block.richText?.runs ?? [{text: block.content, attributes: {}}];
}
export const styleNames = (run: ScriptTextRun): string[] => (run.attributes.Style ?? '').split('+').filter(Boolean);

export function sliceRuns(runs: readonly ScriptTextRun[], start: number, end: number): ScriptTextRun[] {
    let offset = 0;
    return runs.flatMap(run => {
        const text = run.text.slice(Math.max(0, start - offset), Math.max(0, end - offset));
        offset += run.text.length;
        return text ? [{text, attributes: {...run.attributes}}] : [];
    });
}
export function compactRuns(runs: ScriptTextRun[]): ScriptTextRun[] {
    const result: ScriptTextRun[] = [];
    for (const run of runs) {
        if (!run.text) continue;
        const last = result.at(-1);
        const keys = Object.keys(run.attributes);
        if (last && keys.length === Object.keys(last.attributes).length
            && keys.every(key => run.attributes[key] === last.attributes[key])) last.text += run.text;
        else result.push({text: run.text, attributes: {...run.attributes}});
    }
    return result;
}
export function withRuns(block: ScriptBlockData, runs: ScriptTextRun[], paragraphAttributes = block.richText?.paragraphAttributes ?? {}): ScriptBlockData {
    const merged = compactRuns(runs);
    return {...block, content: merged.map(run => run.text).join(''),
        richText: {...block.richText,schemaVersion: 1, runs: merged, paragraphAttributes: {...paragraphAttributes}}};
}
export const deletionMarksFor=(block:Pick<ScriptBlockData,'richText'>)=>block.richText?.deletionMarks??[];
export function withDeletionMarks<T extends ScriptBlockData>(block:T,marks:NonNullable<ScriptRichText['deletionMarks']>):T {
    const unanchored={...block,richText:block.richText?{...block.richText,deletionMarks:undefined}:undefined};
    const richText={...withRuns(unanchored,runsFor(unanchored)).richText!};
    const unique=[...new Map(marks.map(m=>[`${m.offset}:${m.revisionId}`,{...m}])).values()].sort((a,b)=>a.offset-b.offset);
    if(unique.length)richText.deletionMarks=unique;else delete richText.deletionMarks;
    validateRichText(richText,block.content);return {...block,richText};
}

/** A single text replacement preserves unchanged spans; inserted text inherits
 * the preceding run (or the following run at paragraph start). UTF-16 offsets
 * match DOM selections; boundaries never split a surrogate pair. */
export function replaceBlockText(block: ScriptBlockData, content: string, revisionId?:string,trackDeletions=false,edit?:ScriptTextEdit): ScriptBlockData {
    const observed=edit?.before===block.content?observedTextEdit(block.content,content,edit.start,edit.end):undefined;
    const exact=observed&&observed.text===edit?.text?observed:undefined;
    if (block.content === content && (!exact||exact.start===exact.end)) return block;
    if (!block.richText && !revisionId) return {...block, content};
    const old = block.content;
    let start = 0;
    while (start < Math.min(old.length, content.length) && old[start] === content[start]) start++;
    if (start && /[\uD800-\uDBFF]/.test(old[start - 1])) start--;
    let end = old.length, nextEnd = content.length;
    while (end > start && nextEnd > start && old[end - 1] === content[nextEnd - 1]) { end--; nextEnd--; }
    if (end < old.length && /[\uDC00-\uDFFF]/.test(old[end])) { end++; nextEnd++; }
    // FDSPText 00972440 receives the actual mutation position. A string diff
    // alone cannot identify which occurrence of repeated text was edited.
    if(exact){start=exact.start;end=exact.end;nextEnd=start+exact.text.length;}
    const runs = runsFor(block);
    const inherited = sliceRuns(runs, Math.max(0, start - 1), start).at(-1)
        ?? sliceRuns(runs, start, start + 1)[0] ?? runs[0];
    const attributes={...inherited?.attributes};
    delete attributes.RevisionID;if(revisionId)attributes.RevisionID=revisionId;
    const result=withRuns(block, [...sliceRuns(runs, 0, start),
        {text: content.slice(start, nextEnd), attributes},
        ...sliceRuns(runs, end, old.length)]);
    // Native FDDeletionMgr 00753f90 leaves an anchor exactly at the insertion point on its left.
    const marks=deletionMarksFor(block).map(m=>{const afterDelete=m.offset<=start?m.offset:m.offset<end?start:m.offset-(end-start);
        return {...m,offset:afterDelete>start?afterDelete+nextEnd-start:afterDelete};});
    if(trackDeletions&&revisionId&&end>start)marks.push({offset:start,revisionId});
    return marks.length||deletionMarksFor(block).length ? withDeletionMarks(result,marks) : result;
}
/** Split removes a selected range, then partitions anchors; it never marks the moved tail as deleted. */
export function splitRichParagraph(block:ScriptBlockData,start:number,end:number,next:ScriptBlockData,revisionId?:string,track=false):[ScriptBlockData,ScriptBlockData] {
    start=Math.max(0,Math.min(start,block.content.length));end=Math.max(start,Math.min(end,block.content.length));
    if(/[\uDC00-\uDFFF]/.test(block.content[start]??''))start--;
    if(/[\uDC00-\uDFFF]/.test(block.content[end]??''))end++;
    const content=block.content.slice(0,start)+block.content.slice(end);
    const edited=replaceBlockText(block,content,revisionId,track,observedTextEdit(block.content,content,start,end));
    const marks=deletionMarksFor(edited);
    const before=withRuns(edited,sliceRuns(runsFor(edited),0,start));
    const after=withRuns(next,sliceRuns(runsFor(edited),start,edited.content.length));
    return [withDeletionMarks({...before,richText:{...before.richText!,deletionMarks:undefined}},marks.filter(m=>m.offset<start||m.offset===start&&end>start)),
        withDeletionMarks(after,marks.filter(m=>m.offset>start||m.offset===start&&end===start).map(m=>({...m,offset:m.offset-start})))];
}
export function mergeRichParagraphs(before:ScriptBlockData,after:ScriptBlockData):ScriptBlockData {
    const separator=before.content&&after.content?' ':'';
    const merged=withRuns(before,[...runsFor(before),{text:separator,attributes:{}},...runsFor(after)]);
    return withDeletionMarks(merged,[...deletionMarksFor(before),...deletionMarksFor(after).map(m=>({...m,offset:m.offset+before.content.length+separator.length}))]);
}
export function removeRichParagraphs<T extends ScriptBlockData>(parts:readonly T[],ids:ReadonlySet<string>,revisionId?:string,track=false):T[] {
    const kept=parts.filter(p=>!ids.has(p.id));if(!kept.length)return [...parts];
    for(let index=0;index<parts.length;index++){
        const removed=parts[index];if(!ids.has(removed.id))continue;
        const following=parts.slice(index+1).find(p=>!ids.has(p.id)),targetId=(following??kept.at(-1)!).id;
        const targetIndex=kept.findIndex(p=>p.id===targetId),target=kept[targetIndex],offset=following?0:target.content.length;
        const marks=deletionMarksFor(removed).map(m=>({...m,offset}));
        if(track&&revisionId&&removed.content.length)marks.push({offset,revisionId});
        if(marks.length)kept[targetIndex]=withDeletionMarks(target,[...deletionMarksFor(target),...marks]);
    }
    return kept;
}

export function toggleTextStyle(block: ScriptBlockData, start: number, end: number, style: TextStyle): ScriptBlockData {
    if (start < 0 || end > block.content.length || start >= end) return block;
    if (start > 0 && /[\uDC00-\uDFFF]/.test(block.content[start])) start--;
    if (end < block.content.length && /[\uDC00-\uDFFF]/.test(block.content[end])) end++;
    const runs = runsFor(block), selection = sliceRuns(runs, start, end);
    const remove = selection.every(run => styleNames(run).includes(style));
    const styled = selection.map(run => {
        const names = styleNames(run).filter(name => name !== style);
        if (!remove) names.push(style);
        const attributes = {...run.attributes};
        if (names.length) attributes.Style = names.join('+'); else delete attributes.Style;
        return {text: run.text, attributes};
    });
    return withRuns(block, [...sliceRuns(runs, 0, start), ...styled, ...sliceRuns(runs, end, block.content.length)]);
}

/** Share the existing physical layout; Courier style variants use the same
 * glyph advance. Trim consumed wrap/newline whitespace and expand tabs exactly
 * as the layout engine does, without moving formatting to adjacent lines. */
export function printLineRuns(line: PrintLine, block?: ScriptBlockData): ScriptTextRun[] {
    if (block?.dualDialogue && line.kind==='content') {
        const [side,...identity]=line.field.split('.');
        if (side==='left' || side==='right') {
            const part=block.dualDialogue[side].find(p => p.id===identity.join('.'));
            if (part) return printLineRuns({...line,field:'content'},part);
        }
    }
    if (!block?.richText || line.kind !== 'content' || line.field !== 'content') return [{text: line.text, attributes: {}}];
    let remaining = line.text.length;
    return sliceRuns(runsFor(block), line.sourceStart, line.sourceEnd).flatMap(run => {
        const size=scriptFontSize(run.attributes.Size,line.fontSize??12);
        const tabs=Math.min(4,Math.max(1,Math.floor((line.width+1e-6)/(size*.6))));
        const text = run.text.replace(/[\r\n]/g, '').replace(/\t/g, ' '.repeat(tabs)).slice(0, remaining);
        remaining -= text.length;
        return text ? [{text, attributes: run.attributes}] : [];
    });
}
